import { useState, useEffect, useCallback } from 'react';
import { getCountries } from '../services/profileService';
import { useUserStore } from '../../auth/store/userStore';
import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';
import { handleApiError } from '../../../shared/services/toast';
import { findDefaultCountry, toRegionOption, findRegionOption } from '../../../shared/hooks/useCountryRegionOptions';

// Helper function to find item by ID or name
const findItemByIdOrName = (items, profileValue, profileId) => {
  if (!profileValue && !profileId) return null;

  // Try ID first if available
  if (profileId) {
    return items.find(item => item.id === profileId);
  }

  // Then by name — or, for a region, by the short code its Address Line 4 holds. Checked before the
  // numeric-id fallback below so a numeric short code is never mistaken for an option id.
  const byCodeOrName = findRegionOption(items, profileValue);
  if (byCodeOrName) return byCodeOrName;

  const isNumeric = /^\d+$/.test(profileValue);
  return isNumeric ? items.find(item => item.value === profileValue) : undefined;
};

export function useBillingAddress({ setValue, watch, isSubmitted = false }) {
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [countries, setCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  const { 
    profile, selectedCountry: storedCountry,selectedRegion: storedRegion,regions, 
    setSelectedCountry, setSelectedRegion,setRegions: setUserStoreRegions} = useUserStore();
  
  const selectedCountry = watch('country');
  const selectedRegion = watch('region');

  // Format data helper
  const formatItems = useCallback((items) => 
    items.map(item => ({
      id: item.id,
      value: item.id.toString(),
      label: item.name,
      name: item.name
    }))
  , []);

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        const countriesData = await getCountries();
        setCountries(formatItems(countriesData || []));
      } catch (error) {
        handleApiError(error, 'Failed to load countries.');
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [formatItems]);

  // Set country from profile when countries are loaded
  useEffect(() => {
    // A site with no saved country starts on United States. Waits for the profile itself, so a
    // country list that loads first can never claim the field before the saved country arrives.
    if (countries.length > 0 && !selectedCountry && profile && !profile.billingAddress?.country) {
      const defaultCountry = findDefaultCountry(countries);
      if (defaultCountry) {
        setValue('country', defaultCountry.value);
        setSelectedCountry(defaultCountry);
      }
      return;
    }

    if (countries.length > 0 && !selectedCountry && profile?.billingAddress?.country) {
      const foundCountry = findItemByIdOrName(
        countries, 
        profile.billingAddress.country,
        profile.billingAddress.countryId
      ) || countries[0];

      /* istanbul ignore else -- foundCountry is always truthy here: the `|| countries[0]` fallback resolves because this block only runs when countries.length > 0 */
      if (foundCountry) {
        setValue('country', foundCountry.value);
        setSelectedCountry(foundCountry);
      }
    }
  }, [countries, selectedCountry, profile, setValue, setSelectedCountry]);

  // Load regions when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setUserStoreRegions([]);
      setAvailableRegions([]);
      setValue('region', '');
      return;
    }

    const loadRegions = async () => {
      try {
        setLoadingRegions(true);
        const countryId = typeof selectedCountry === 'string' ? Number.parseInt(selectedCountry) : selectedCountry;
        const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_REGIONS_BY_COUNTRY(countryId));
        const regionsData = response.data?.data?.regions || [];
        
        setUserStoreRegions(regionsData);
        setAvailableRegions(regionsData.map(toRegionOption));
        
        if (regionsData.length === 0) {
          setValue('region', 'no-regions');
        }
      } catch (error) {
        handleApiError(error, 'Failed to load regions.');
        setUserStoreRegions([]);
        setAvailableRegions([]);
        setValue('region', 'no-regions');
      } finally {
        setLoadingRegions(false);
      }
    };

    loadRegions();
  }, [selectedCountry, setValue, setUserStoreRegions, formatItems]);

  // Set region from profile when regions are loaded. No saved region for this country (a new firm,
  // or the user just switched country) leaves Region empty ("No region/state") rather than
  // picking the first option — Region is optional, so it's never chosen on the user's behalf.
  useEffect(() => {
    if (availableRegions.length > 0 && profile?.billingAddress) {
      const foundRegion = findItemByIdOrName(
        availableRegions,
        profile.billingAddress.region,
        profile.billingAddress.regionId
      );

      if (foundRegion) {
        setValue('region', foundRegion.value);
        setSelectedRegion(foundRegion);
      } else {
        // Clears a region left over from the previous country, which no longer matches an option.
        setValue('region', '');
      }
    }
  }, [availableRegions, profile, setValue, setSelectedRegion]);

  // Sync form values with store
  useEffect(() => {
    if (selectedCountry && countries.length > 0) {
      const country = countries.find(c => c.value === selectedCountry.toString());
      if (country && (!storedCountry || storedCountry.id.toString() !== selectedCountry.toString())) {
        setSelectedCountry(country);
      }
    }
  }, [selectedCountry, countries, storedCountry, setSelectedCountry]);

  useEffect(() => {
    if (selectedRegion && regions.length > 0) {
      const region = regions.find(r => r.id.toString() === selectedRegion.toString());
      if (region && (!storedRegion || storedRegion.id.toString() !== selectedRegion.toString())) {
        setSelectedRegion(region);
      }
    }
  }, [selectedRegion, regions, storedRegion, setSelectedRegion]);

  // setValue never re-runs validation on its own (unlike a registered input's onChange), so after
  // a failed save "Country is required" stayed on screen once a country was picked. Re-validates
  // only after a first save attempt, so nothing is flagged before then.
  const handleCountryChange = useCallback((value) => {
    setValue('country', value, { shouldValidate: isSubmitted });
  }, [setValue, isSubmitted]);

  const handleRegionChange = useCallback((value) => {
    setValue('region', value, { shouldValidate: isSubmitted });
  }, [setValue, isSubmitted]);

  return {
    countries,availableRegions,selectedCountry,selectedRegion,loadingCountries,loadingRegions,
    handleCountryChange,handleRegionChange,
    noRegionsOption: {
      id: 'no-regions',
      value: 'no-regions',
      label: 'No regions available',
      name: 'No regions available'
    }
  };
}
